import { computed, ref, type ComputedRef, type Ref } from 'vue';

export const THEME_STORAGE_KEY = 'chametw.admin.theme';

export const ThemePreference = {
  Light: 'light',
  Dark: 'dark',
  System: 'system',
} as const;
export type ThemePreference = (typeof ThemePreference)[keyof typeof ThemePreference];

export type ResolvedTheme = 'light' | 'dark';

function isPreference(value: string | null): value is ThemePreference {
  return value === 'light' || value === 'dark' || value === 'system';
}

export function systemTheme(): ResolvedTheme {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
    return 'light';
  }
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

export function readStoredPreference(): ThemePreference {
  if (typeof localStorage === 'undefined') {
    return ThemePreference.System;
  }
  const stored = localStorage.getItem(THEME_STORAGE_KEY);
  return isPreference(stored) ? stored : ThemePreference.System;
}

export function resolveTheme(preference: ThemePreference): ResolvedTheme {
  return preference === ThemePreference.System ? systemTheme() : preference;
}

export function applyTheme(theme: ResolvedTheme): void {
  if (typeof document === 'undefined') {
    return;
  }
  document.documentElement.dataset['theme'] = theme;
  document.documentElement.style.colorScheme = theme;
}

const preference: Ref<ThemePreference> = ref(readStoredPreference());
const resolved: Ref<ResolvedTheme> = ref(resolveTheme(preference.value));

export interface ThemeApi {
  readonly preference: Ref<ThemePreference>;
  readonly resolved: Ref<ResolvedTheme>;
  readonly isDark: ComputedRef<boolean>;
  setPreference(next: ThemePreference): void;
  toggle(): void;
}

export function useTheme(): ThemeApi {
  function setPreference(next: ThemePreference): void {
    preference.value = next;
    resolved.value = resolveTheme(next);
    applyTheme(resolved.value);
    if (typeof localStorage !== 'undefined') {
      localStorage.setItem(THEME_STORAGE_KEY, next);
    }
  }

  function toggle(): void {
    setPreference(resolved.value === 'dark' ? ThemePreference.Light : ThemePreference.Dark);
  }

  return {
    preference,
    resolved,
    isDark: computed(() => resolved.value === 'dark'),
    setPreference,
    toggle,
  };
}

export function initTheme(): void {
  const initial = readStoredPreference();

  preference.value = initial;
  resolved.value = resolveTheme(initial);
  applyTheme(resolved.value);
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
    return;
  }
  window.matchMedia('(prefers-color-scheme: dark)').addEventListener('change', () => {
    if (preference.value === ThemePreference.System) {
      resolved.value = systemTheme();
      applyTheme(resolved.value);
    }
  });
}
